/**
 * @file ConsentTextsPage.tsx
 * @description «Платформа → Согласие на cookie» (phase19.16, Р3г, v0.19.62;
 * решение пользователя 2026-10-23: тексты баннера cookie правит
 * платформенный администратор без релиза; uiux-r3g-consent-texts §1, §2, §5).
 *
 * Список: действующая редакция баннера («На сайтах»), черновик этого
 * браузера, прежние редакции; ниже — окно о риске режима «Уведомление» (у
 * него своя нумерация, db-design-r3g-texts §1). «Смотреть» — шторка с
 * полями и предпросмотром, «Взять за основу» — новая редакция от прежней.
 * Право `PLATFORM_MANAGE_PERSONAL_DATA` проверяет маршрут.
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { Plus } from "lucide-react";
import { useId, useState } from "react";

import { ConsentLiveCard } from "../../components/platform/consent-texts/ConsentLiveCard";
import { ConsentRevisionSheet } from "../../components/platform/consent-texts/ConsentRevisionSheet";
import { Button } from "../../components/ui/button";
import { CabinetPageHeader } from "../../components/ui/CabinetPageHeader";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { ListError } from "../../components/ui/ListStates";
import { Skeleton } from "../../components/ui/skeleton";
import { useConsentDraftActions } from "../../hooks/platform/useConsentDraftActions";
import { useConsentTextsOverview } from "../../hooks/platform/useConsentTextsOverview";
import { loadConsentDraft } from "../../lib/platform/consent-texts-draft";

/** Подписи карточек видов. */
const KIND_TITLE: Readonly<Record<ConsentTextKind, string>> = {
  BANNER: "Баннер cookie",
  NOTICE_RISK: "Окно о риске режима «Уведомление»",
};

const NO_REVISIONS = "Редакций нет — исходная редакция 1 не загружена";

interface NewButtonProps {
  label: string;
  disabled: boolean;
  /** Почему неактивна; есть — подключается через aria-describedby (ревью М7). */
  reason?: string;
  onClick: () => void;
}

function NewRevisionButton({ label, disabled, reason, onClick }: NewButtonProps) {
  const reasonId = useId();
  const described = disabled && reason ? reasonId : undefined;
  return (
    <>
      <Button variant="brand" disabled={disabled} aria-describedby={described} onClick={onClick}>
        <Plus className="size-4" aria-hidden />
        {label}
      </Button>
      {described ? (
        <span id={described} className="sr-only">
          {reason}
        </span>
      ) : null}
    </>
  );
}

export function ConsentTextsPage() {
  const { loading, error, data, reload } = useConsentTextsOverview();
  const { confirmApi, startNew, dropDraft } = useConsentDraftActions();
  const [viewing, setViewing] = useState<{ kind: ConsentTextKind; number: number } | null>(null);
  const noBanner = !!data && !data.BANNER.current;

  return (
    <div className="flex flex-col gap-6">
      <CabinetPageHeader
        title="Согласие на cookie"
        subtitle="Фразы баннера, которые платформа ставит на сайтах"
        action={
          <NewRevisionButton
            label="Новая редакция"
            disabled={!data || noBanner}
            reason={noBanner ? NO_REVISIONS : undefined}
            onClick={() => void startNew("BANNER")}
          />
        }
      />
      {loading && !data ? (
        <div aria-busy="true" aria-label="Загрузка текстов" className="flex flex-col gap-3">
          <Skeleton className="h-32 w-full" />
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : null}
      {error ? <ListError message={error} onRetry={reload} /> : null}
      {data
        ? (["BANNER", "NOTICE_RISK"] as const).map((kind) => (
            <ConsentLiveCard
              key={kind}
              kind={kind}
              title={KIND_TITLE[kind]}
              overview={data[kind]}
              draft={loadConsentDraft(kind)}
              onView={(number) => setViewing({ kind, number })}
              onDropDraft={() => void dropDraft(kind)}
              action={
                kind === "NOTICE_RISK" ? (
                  <NewRevisionButton
                    label="Новая редакция окна"
                    disabled={!data[kind].current}
                    reason={data[kind].current ? undefined : NO_REVISIONS}
                    onClick={() => void startNew(kind)}
                  />
                ) : undefined
              }
            />
          ))
        : null}
      <ConsentRevisionSheet
        kind={viewing?.kind ?? "BANNER"}
        number={viewing?.number ?? null}
        onClose={() => setViewing(null)}
        onTakeBase={(number) => {
          const kind = viewing?.kind ?? "BANNER";
          setViewing(null);
          void startNew(kind, number);
        }}
      />
      <ConfirmDialog request={confirmApi.request} onSettle={confirmApi.settle} onDismiss={confirmApi.dismiss} />
    </div>
  );
}
